"use client";

import { cn } from "@/lib/utils";
import { getAuthTopic } from "@/store/auth";
import { findTopicById, TopicItem, useTopicStore } from "@/store/topic";
import { useUserInfo } from "@/store/user-info";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef } from "react";
import { ScrollContainer } from "./scroll-container";

export default function TopicTabs( props: any = {} ) {
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const topicIdParam = searchParams.get( "topicId" );
    const { userId } = useUserInfo();
    const scrollContainerRef = useRef<HTMLDivElement>( null );
    const tabRefs = useRef<Map<number, HTMLDivElement>>( new Map() );

    const {
        normalTopics,
        liveTopics,
        fetchLiveTopics,
        fetchNormalTopics,
        normalTopicChains,
        liveTopicChains,
        setSelectedLiveTopic,
        setSelectedNormalTopic,
        selectedLiveTopic,
        selectedNormalTopic,
    } = useTopicStore();

    // 判断是否在 Live 页
    const isLivePage = pathname === "/live";

    // 判断是否在 Home 页
    const isHomePage = pathname === "/";

    const topicChains = useMemo( () => isLivePage ? liveTopicChains : normalTopicChains, [
        isLivePage,
        selectedLiveTopic,
        selectedNormalTopic,
        liveTopicChains,
        normalTopicChains,
    ] );

    const currentTopics = isLivePage ? liveTopics : normalTopics;
    const topicsSortList = useMemo( () => {
        const authTopic = getAuthTopic( userId );
        const authOrder = new Map( ( authTopic || [] ).map( ( id, index ) => [ id, index ] ) );

        // 创建 topics 的副本以避免修改原始数据
        const sortedTopics = [ ...currentTopics ];

        const getPriority = ( topic: TopicItem ) => {
            const isTrending = topic.name === "Trending" ? 1 : 0;
            const authRank = authOrder.has( topic.id ) ? -( authOrder.get( topic.id ) as number ) : 0;
            const sortRank = topic.sort ?? Number.MAX_SAFE_INTEGER;
            return { isTrending, authRank, sortRank, idRank: topic.id };
        };

        sortedTopics.sort( ( a, b ) => {
            const pa = getPriority( a );
            const pb = getPriority( b );

            return (
                pb.isTrending - pa.isTrending // Trending first
                || pb.authRank - pa.authRank // Auth order (smaller index first)
                || pa.sortRank - pb.sortRank // Explicit sort value
                || pa.idRank - pb.idRank // Fallback to id
            );
        } );

        return sortedTopics;
    }, [ userId, currentTopics ] );

    // 获取一级菜单数据
    useEffect( () => {
        if ( isLivePage && !liveTopics.length ) fetchLiveTopics();
        if ( !isLivePage && !normalTopics.length ) fetchNormalTopics();
    }, [ pathname ] );

    useEffect( () => {
        if ( pathname !== "/main-event-detail" || isLivePage || !normalTopics.length ) return;
        if ( !topicIdParam ) return;
        const topicId = Number( topicIdParam );
        if ( !Number.isFinite( topicId ) ) return;
        const topic = findTopicById( normalTopics, topicId );
        if ( !topic || useTopicStore.getState().selectedNormalTopic?.id === topic.id ) return;
        setSelectedNormalTopic( topic );
    }, [ isLivePage, normalTopics, pathname, setSelectedNormalTopic, topicIdParam ] );

    // 处理一级菜单点击
    const handleTopicClick = ( topic: TopicItem ) => {
        let target = topic;
        while ( target.children && target.children.length ) {
            target = target.children[0];
        }

        if ( isLivePage ) {
            setSelectedLiveTopic( target );
        } else {
            setSelectedNormalTopic( target );
        }

        // 只有在 Search/Portfolio 等其他页面时才跳转到 Home 页
        // Home 页和 Live 页保持在当前页
        if ( !isHomePage && !isLivePage ) {
            router.push( "/" );
        }
    };
    useEffect( () => {
        // 居中滚动到被点击的 tab
        const tabElement = tabRefs.current.get( topicChains[0]?.id );
        if ( tabElement && scrollContainerRef.current ) {
            const containerWidth = scrollContainerRef.current.clientWidth;
            const tabLeft = tabElement.offsetLeft;
            const tabWidth = tabElement.offsetWidth;
            const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

            scrollContainerRef.current.scrollTo( { left: scrollLeft, behavior: "smooth" } );
        }
    }, [ topicChains ] );

    // 只在首页和 Live 页显示
    if (
        ![ "/", "/live", "/main-event-detail", "/search", "/portfolio", "/leaderboard", "/event" ].includes( pathname )
    ) {
        return null;
    }

    return (
        <>
            <div className="bg-surface-raised px-4">
                <ScrollContainer
                    ref={scrollContainerRef}
                    showGradient={true}
                    gap="gap-3"
                    className="h-11 items-center"
                    gradientColor="var(--ui-surface-raised)"
                >
                    {topicsSortList.map( ( topic ) => (
                        <div
                            ref={( el ) => {
                                if ( el ) {
                                    tabRefs.current.set( topic.id, el );
                                } else {
                                    tabRefs.current.delete( topic.id );
                                }
                            }}
                            className={cn(
                                "relative flex-shrink-0 cursor-pointer transition-colors whitespace-nowrap before:content-[attr(data-text)] text-sm before:font-bold before:h-0 before:block before:overflow-hidden before:invisible text-t3 font-semibold",
                                topicChains[0]?.id === topic.id && "font-bold text-t1",
                            )}
                            key={topic.id}
                            data-text={topic.name}
                            onClick={() => handleTopicClick( topic )}
                        >
                            {topic.name}
                        </div>
                    ) )}
                </ScrollContainer>
            </div>
            <div className="flex justify-center">
                <div className="h-px bg-border w-full "></div>
            </div>
        </>
    );
}
